import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  NavReconPipelineMetadata,
  NavReconPipelineSnapshot,
  getNavReconRiskMetadataStatusLabel,
} from '../../../../../api/rs-hg/nav-recon/types';
import {
  NavReconPipelineAggregateStatus,
  NavReconPipelineStageMenuItem,
  NAV_RECON_PIPELINE_AGGREGATE_STATUS_LABEL,
  getNavReconPipelineAggregateStatus,
  getNavReconPipelineButtonStatus,
  getPipelineStageMenuItems,
} from '../pipelineMetadata';
import { cn } from '../../../../../utilities/cn';

const MENU_GAP = 8;
const VIEWPORT_PAD = 8;
const PANEL_HEIGHT_PX = 425; // ~36rem — fixed popover height

type PanelCoords = {
  left: number;
  top: number;
};

const getPanelWidth = () =>
  Math.min(28 * 16, window.innerWidth - 24);

interface Props {
  fundName: string;
  metadata: NavReconPipelineMetadata | undefined;
}

const formatTimestamp = (value: string | null | undefined) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const snapshotStatusClasses: Record<string, string> = {
  loading: 'border-amber-200 bg-amber-50 text-amber-800',
  succeeded: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  system_error: 'border-red-200 bg-red-50 text-red-800',
};

const getSnapshotStatusClass = (status: string | null | undefined) =>
  snapshotStatusClasses[status?.toLowerCase() ?? ''] ??
  'border-gray-200 bg-gray-50 text-gray-700';

const aggregateStatusClasses: Record<NavReconPipelineAggregateStatus, string> = {
  error: 'border-red-200 bg-red-50 text-red-800',
  complete: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  in_progress: 'border-amber-200 bg-amber-50 text-amber-800',
  not_started: 'border-gray-200 bg-gray-50 text-gray-600',
};

const aggregateStatusBadge = (status: NavReconPipelineAggregateStatus) => (
  <span
    className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${aggregateStatusClasses[status]}`}
  >
    {NAV_RECON_PIPELINE_AGGREGATE_STATUS_LABEL[status]}
  </span>
);

const stageBadge = (item: NavReconPipelineStageMenuItem) => {
  if (item.state === 'succeeded') {
    return (
      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
        Complete
      </span>
    );
  }
  if (item.state === 'error') {
    return (
      <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800">
        Error
      </span>
    );
  }
  if (item.state === 'loading' || item.state === 'current') {
    return (
      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
        In progress
      </span>
    );
  }
  return (
    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
      Not started
    </span>
  );
};

const stageSectionClassName = (item: NavReconPipelineStageMenuItem) => {
  if (item.state === 'error') {
    return 'rounded-lg border border-red-200 bg-red-50/40 px-3 py-2.5 shadow-sm';
  }
  if (item.state === 'loading' || item.state === 'current') {
    return 'rounded-lg border border-amber-200 bg-amber-50/30 px-3 py-2.5 shadow-sm';
  }
  return 'rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm';
};

const stageSubline = (item: NavReconPipelineStageMenuItem): string => {
  if (item.state === 'succeeded') {
    const snapshotLabel =
      item.snapshotCount === 1
        ? '1 snapshot'
        : `${item.snapshotCount} snapshots`;
    return item.latestSnapshotLabel
      ? `${snapshotLabel} - Latest ${item.latestSnapshotLabel}`
      : snapshotLabel;
  }

  if (item.state === 'error') {
    const snapshotLabel =
      item.snapshotCount === 1
        ? '1 snapshot'
        : `${item.snapshotCount} snapshots`;
    return item.latestSnapshotLabel
      ? `${snapshotLabel} - Latest ${item.latestSnapshotLabel}`
      : snapshotLabel;
  }

  if (item.state === 'loading') {
    return item.latestSnapshotLabel
      ? `Latest snapshot still processing (${item.latestSnapshotLabel}).`
      : 'Latest snapshot is still processing.';
  }

  if (item.state === 'current') {
    return 'Waiting for this stage to finish.';
  }

  return 'Runs after earlier stages complete.';
};

const SnapshotHistoryItem: React.FC<{
  snapshot: NavReconPipelineSnapshot;
}> = ({ snapshot }) => {
  const snapshotStatus = snapshot.status?.toString() || null;

  return (
    <li className="border-b border-gray-100 last:border-b-0">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <time className="m-0 min-w-0 truncate font-mono text-[0.6875rem] font-medium tabular-nums text-gray-800">
          {formatTimestamp(snapshot.snapshot_timestamp)}
        </time>
        {snapshotStatus ? (
          <span
            className={`shrink-0 rounded border px-1.5 py-0.5 text-[0.625rem] font-semibold ${getSnapshotStatusClass(snapshotStatus)}`}
          >
            {getNavReconRiskMetadataStatusLabel(snapshotStatus)}
          </span>
        ) : null}
      </div>

      {snapshot.client_audit_by_name || snapshot.client_audit_timestamp ? (
        <p className="m-0 px-2 pb-1.5 text-[0.6875rem] leading-snug text-gray-500">
          <span className="font-medium text-gray-600">Audit</span>
          {snapshot.client_audit_by_name ? (
            <>
              {' '}
              <span className="text-gray-800">
                {snapshot.client_audit_by_name}
              </span>
            </>
          ) : null}
          {snapshot.client_audit_timestamp ? (
            <>
              <span className="text-gray-400"> · </span>
              {formatTimestamp(snapshot.client_audit_timestamp)}
            </>
          ) : null}
        </p>
      ) : null}

      {snapshot.client_comment ? (
        <p className="m-0 whitespace-pre-wrap px-2 pb-1.5 text-[0.6875rem] leading-snug text-gray-600">
          {snapshot.client_comment}
        </p>
      ) : null}
    </li>
  );
};

const SnapshotHistory: React.FC<{
  item: NavReconPipelineStageMenuItem;
}> = ({ item }) => {
  if (item.snapshotCount === 0) return null;

  const countLabel =
    item.snapshotCount === 1 ? '1 snapshot' : `${item.snapshotCount} snapshots`;

  return (
    <details className="mt-1 overflow-hidden rounded-md border border-gray-200 bg-gray-50/90 open:[&>summary_.pipeline-hist-chevron]:rotate-90">
      <summary className="flex cursor-pointer list-none items-center gap-1 px-2 py-1.5 text-[0.6875rem] font-semibold text-gray-700 transition hover:bg-gray-100/80 [&::-webkit-details-marker]:hidden">
        <span
          className="pipeline-hist-chevron inline-block text-gray-400 transition-transform duration-150"
          aria-hidden
        >
          ›
        </span>
        <span>Snapshot history</span>
        <span className="font-normal text-gray-500">({countLabel})</span>
      </summary>
      <div className="border-t border-gray-200 bg-white py-0.5">
        <ol className="m-0 list-none p-0">
          {item.snapshots.map((snapshot, index) => (
            <SnapshotHistoryItem
              key={`${snapshot.snapshot_timestamp}-${index}`}
              snapshot={snapshot}
            />
          ))}
        </ol>
      </div>
    </details>
  );
};

const NavReconPipelineStagesMenu: React.FC<Props> = ({
  fundName,
  metadata,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PanelCoords | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const items = getPipelineStageMenuItems(metadata);
  const aggregateStatus = getNavReconPipelineAggregateStatus(metadata);
  const pipelineButtonStatus = getNavReconPipelineButtonStatus(metadata);

  const computePanelCoords = useCallback((): PanelCoords | null => {
    const btn = buttonRef.current;
    if (!btn) return null;

    const br = btn.getBoundingClientRect();
    const panelWidth = getPanelWidth();

    // Default: align top with trigger; shift up if fixed height would overflow viewport.
    let top = br.top;
    if (top + PANEL_HEIGHT_PX > window.innerHeight - VIEWPORT_PAD) {
      top = window.innerHeight - VIEWPORT_PAD - PANEL_HEIGHT_PX;
    }
    top = Math.max(VIEWPORT_PAD, top);

    let left = br.left - MENU_GAP - panelWidth;
    if (left < VIEWPORT_PAD) {
      left = br.right - panelWidth;
    }
    if (left + panelWidth > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(
        VIEWPORT_PAD,
        window.innerWidth - VIEWPORT_PAD - panelWidth,
      );
    }

    return {
      left: Math.round(left),
      top: Math.round(top),
    };
  }, []);

  const positionPanel = useCallback(() => {
    const nextCoords = computePanelCoords();
    if (nextCoords) {
      setCoords(nextCoords);
    }
  }, [computePanelCoords]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;

    const onResize = () => positionPanel();
    const onScroll = (e: Event) => {
      const target = e.target;
      if (
        target instanceof Node &&
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    /* Capture phase: pipeline buttons use stopPropagation() so row/detail logic
     * does not see the event; we still need other open menus to close. */
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [open]);

  const panel = open && coords ? (
    <div
      ref={panelRef}
      className="fixed z-[10001] flex w-[min(28rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left text-sm text-gray-800 shadow-xl ring-1 ring-black/5"
      style={{
        left: coords.left,
        top: coords.top,
        height: PANEL_HEIGHT_PX,
      }}
      role="dialog"
      aria-label={`NAV recon pipeline stages for ${fundName || 'fund'}`}
    >
      <div className="shrink-0 border-b border-gray-100 bg-gray-50/80 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 text-xs font-bold uppercase tracking-wide text-mainblue">
            Pipeline stages
          </p>
          {aggregateStatusBadge(aggregateStatus)}
        </div>
        <p className="m-0 mt-1 min-w-0 truncate text-sm font-semibold text-gray-900">
          {fundName || 'Unknown fund'}
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-y-contain px-3 py-3">
        {items.map((item) => (
          <section
            key={item.stageKey}
            className={stageSectionClassName(item)}
          >
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
              <p className="m-0 min-w-0 flex-1 text-sm font-semibold text-gray-900">
                {item.title}
              </p>
              {stageBadge(item)}
            </div>
            <p className="m-0 mt-1 text-xs leading-snug text-gray-600">
              {stageSubline(item)}
            </p>
            <SnapshotHistory item={item} />
          </section>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-mainblue/30",
          open
            ? "border-mainblue bg-mainblue text-white ring-2 ring-mainblue/30"
            : pipelineButtonStatus === 'error'
              ? "border-red-300 bg-red-50 text-red-700 hover:border-red-400 hover:bg-red-100"
              : pipelineButtonStatus === 'complete'
                ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-100"
                : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800"
        )}
        aria-label={`Pipeline stages for ${fundName || 'fund'}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            setOpen(false);
            setCoords(null);
            return;
          }
          setCoords(computePanelCoords());
          setOpen(true);
        }}
      >
        <span className="sr-only">Open pipeline stages</span>
        <span className="flex items-center gap-0.5" aria-hidden>
          <span className="h-1 w-1 rounded-full bg-current" />
          <span className="h-1 w-1 rounded-full bg-current" />
          <span className="h-1 w-1 rounded-full bg-current" />
        </span>
      </button>
      {typeof document !== 'undefined' && panel
        ? createPortal(panel, document.body)
        : panel}
    </>
  );
};

export default NavReconPipelineStagesMenu;
